import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Avatar from './Avatar';
import axios from 'axios';

const ROLE_STYLES = {
  fondateur: { bg: '#EBF4FF', color: '#2B6CB0', label: 'Fondateur' },
  archon: { bg: '#FAF5FF', color: '#6B46C1', label: 'Archon' },
  lecteur: { bg: '#F7F9FC', color: '#4A5568', label: 'Lecteur' },
  membre: { bg: '#F7F9FC', color: '#4A5568', label: 'Membre' },
};

export default function MembersSidebar({ project, user, token, API, onRefresh }) {
  const [showInvite, setShowInvite] = useState(false);
  const [inviteUsername, setInviteUsername] = useState('');
  const [inviteRole, setInviteRole] = useState('lecteur');
  const [inviteSearch, setInviteSearch] = useState([]);
  const [changingRole, setChangingRole] = useState(null);

  const isOwner = user && project.owner.username === user.username;
  const myRole = project.members.find(m => m.user.username === user?.username)?.role;
  const canInvite = myRole === 'fondateur' || myRole === 'archon';

  const handleInviteSearch = async (q) => {
    setInviteUsername(q);
    if (q.length < 2) { setInviteSearch([]); return; }
    try {
      const res = await axios.get(`${API}/users/search/users?q=${q}`);
      setInviteSearch(res.data.filter(u => !project.members.some(m => m.user.username === u.username)));
    } catch (err) { console.error(err); }
  };

  const handleInvite = async (username) => {
    try {
      await axios.post(`${API}/projects/${project.id}/invite/${username}?role=${inviteRole}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInviteUsername(''); setInviteSearch([]); onRefresh();
    } catch (err) { console.error(err); }
  };

  const handleChangeRole = async (username, role) => {
    setChangingRole(username);
    try {
      await axios.put(`${API}/projects/${project.id}/members/${username}/role?role=${role}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onRefresh();
    } catch (err) { console.error(err); }
    finally { setChangingRole(null); }
  };

  const handleRemove = async (username) => {
    try {
      await axios.delete(`${API}/projects/${project.id}/members/${username}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onRefresh();
    } catch (err) { console.error(err); }
  };

  return (
    <div style={{
      background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)',
      borderRadius: '16px', padding: '18px',
      border: '1px solid var(--border)', boxShadow: 'var(--shadow)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <h3 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--dark)', margin: 0 }}>
          Membres ({project.members.length})
        </h3>
        {canInvite && (
          <button onClick={() => setShowInvite(!showInvite)} style={{
            background: 'var(--blue-bg)', color: 'var(--blue)', border: 'none',
            padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, cursor: 'pointer'
          }}>
            + Inviter
          </button>
        )}
      </div>

      {/* Invite panel */}
      {showInvite && (
        <div style={{ marginBottom: '14px' }}>
          {/* Role selector */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
            {[
              { value: 'archon', label: '⚔️ Archon', desc: 'Peut modifier' },
              { value: 'lecteur', label: '👁️ Lecteur', desc: 'Peut lire' }
            ].map(r => (
              <button key={r.value} onClick={() => setInviteRole(r.value)} style={{
                flex: 1, background: inviteRole === r.value ? ROLE_STYLES[r.value].bg : 'transparent',
                color: inviteRole === r.value ? ROLE_STYLES[r.value].color : 'var(--muted)',
                border: `1.5px solid ${inviteRole === r.value ? ROLE_STYLES[r.value].color : 'var(--border)'}`,
                padding: '6px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                textAlign: 'center'
              }}>
                <div>{r.label}</div>
                <div style={{ fontSize: '10px', opacity: 0.7 }}>{r.desc}</div>
              </button>
            ))}
          </div>

          {/* Search */}
          <div style={{ position: 'relative' }}>
            <input
              value={inviteUsername}
              onChange={e => handleInviteSearch(e.target.value)}
              placeholder="Cherche un membre..."
              style={{
                width: '100%', padding: '8px 12px',
                border: '1.5px solid var(--blue-border)', borderRadius: '10px',
                fontSize: '12px', outline: 'none'
              }}
              onFocus={e => e.target.style.borderColor = 'var(--blue)'}
              onBlur={e => e.target.style.borderColor = 'var(--blue-border)'}
            />
            {inviteSearch.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0,
                background: '#fff', border: '1px solid var(--border)',
                borderRadius: '10px', marginTop: '4px',
                boxShadow: 'var(--shadow-hover)', zIndex: 10, overflow: 'hidden'
              }}>
                {inviteSearch.map(u => (
                  <div key={u.id} onClick={() => handleInvite(u.username)} style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '8px 12px', cursor: 'pointer', transition: 'background .2s'
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--blue-bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <Avatar user={u} size={26} fontSize={9} />
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600 }}>{u.username}</div>
                      <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Inviter comme {inviteRole}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Members list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {project.members.map(member => {
          const roleStyle = ROLE_STYLES[member.role] || ROLE_STYLES.lecteur;
          const isMe = member.user.username === user?.username;
          return (
            <div key={member.id} style={{
              padding: '8px 8px', borderRadius: '10px',
              border: isMe ? '1px solid var(--blue-border)' : '1px solid transparent',
              background: isMe ? 'var(--blue-bg)' : 'transparent',
              transition: 'background .2s'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Link to={`/profile/${member.user.username}`}>
                  <Avatar user={member.user} size={32} fontSize={11} />
                </Link>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Link to={`/profile/${member.user.username}`} style={{
                    fontSize: '13px', fontWeight: 600, color: 'var(--dark)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block'
                  }}>
                    {member.user.username} {isMe && <span style={{ fontSize: '10px', color: 'var(--muted)' }}>(toi)</span>}
                  </Link>
                  <span style={{
                    fontSize: '10px', fontWeight: 600,
                    color: roleStyle.color, background: roleStyle.bg,
                    padding: '1px 6px', borderRadius: '6px', display: 'inline-block', marginTop: '2px'
                  }}>
                    {roleStyle.label}
                  </span>
                </div>

                {/* Role change for owner */}
                {isOwner && member.role !== 'fondateur' && (
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <select
                      value={member.role}
                      onChange={e => handleChangeRole(member.user.username, e.target.value)}
                      disabled={changingRole === member.user.username}
                      style={{
                        border: '1px solid var(--border)', borderRadius: '8px',
                        padding: '3px 6px', fontSize: '11px', cursor: 'pointer',
                        outline: 'none', color: 'var(--text)', background: '#fff'
                      }}
                    >
                      <option value="archon">Archon</option>
                      <option value="lecteur">Lecteur</option>
                    </select>
                    <button
                      onClick={() => handleRemove(member.user.username)}
                      style={{
                        background: 'none', border: 'none', color: 'var(--muted)',
                        cursor: 'pointer', fontSize: '14px', padding: '0 2px',
                        lineHeight: 1
                      }}
                      title="Retirer"
                    >×</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ marginTop: '14px', padding: '10px', background: 'var(--bg)', borderRadius: '10px' }}>
        <div style={{ fontSize: '10px', color: 'var(--muted)', fontWeight: 600, marginBottom: '6px', letterSpacing: '0.5px' }}>RÔLES</div>
        {[
          { role: 'fondateur', desc: 'Contrôle total' },
          { role: 'archon', desc: 'Modifie et invite' },
          { role: 'lecteur', desc: 'Lit et propose' },
        ].map(r => {
          const s = ROLE_STYLES[r.role];
          return (
            <div key={r.role} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <span style={{ fontSize: '10px', fontWeight: 600, color: s.color, background: s.bg, padding: '1px 6px', borderRadius: '6px' }}>
                {s.label}
              </span>
              <span style={{ fontSize: '10px', color: 'var(--muted)' }}>{r.desc}</span>
            </div>
          );
        })}
      </div>

      {!user && (
        <div style={{ marginTop: '10px', padding: '10px', background: 'var(--blue-bg)', borderRadius: '10px', textAlign: 'center' }}>
          <Link to="/login" style={{ fontSize: '12px', color: 'var(--blue)', fontWeight: 600 }}>Se connecter →</Link>
        </div>
      )}
    </div>
  );
}
